import React, { useState } from 'react';
import ReactGA from 'react-ga';
import PropTypes from 'prop-types';
import { Heading, Wizard, Modal, Flex, togatherTheme } from '@feast-it/pesto';
import { useMutation } from '@apollo/react-hooks';
import { gql } from '@apollo/client';
import cookies from 'js-cookie';
import { ThemeProvider } from 'emotion-theming';
import { baseTheme, Button } from '@feast-it/ui';

import Layout from '../../containers/layout';
import HideFreshChat from '../../components/HideFreshChat';
import Meta from '../../components/Meta';
import SupplierWelcome from '../../containers/forms/supplier-account-creation/Welcome';
import Tier1Service from '../../containers/forms/supplier-account-creation/Tier1';
import SupplierContext from '../../context/supplierSignUp';
import { getTier1Verticals } from '../../modules/verticals';
import BusinessName from '../../containers/forms/supplier-account-creation/Name';
import SupplierLocation from '../../containers/forms/supplier-account-creation/Location';
import SocialMediaLinks from '../../containers/forms/supplier-account-creation/ExternalLinks';
import HearingAboutFeastIt from '../../containers/forms/supplier-account-creation/Source';
import SupplierDetails from '../../containers/forms/supplier-account-creation/User';
import { trackEvent, tagManagerTrack } from '../../modules/analytics';
import {
  SUPPLIER_FORM_BACK_CLICKED,
  SUPPLIER_FORM_NEXT_CLICKED,
  SUPPLIER_FORM_CLOSE_CLICKED,
  SUPPLIER_FORM_ABANDONED,
  SUPPLIER_APPLICATION_SUBMIT,
} from '../../constants/analytics';
import paths from '../../constants/paths';
import {
  createSupplierUser,
  createSupplier,
  linkUserSupplier,
} from '../../modules/supplierOnboarding';
import tier1Schema from '../../containers/forms/schema/supplier-sign-up/tier1';
import nameSchema from '../../containers/forms/schema/supplier-sign-up/name';
import locationSchema from '../../containers/forms/schema/supplier-sign-up/location';
import externalLinksSchema from '../../containers/forms/schema/supplier-sign-up/external-links';
import sourceSchema from '../../containers/forms/schema/supplier-sign-up/source.js';
import userSchema from '../../containers/forms/schema/supplier-sign-up/user.js';
import {
  useAnonymousIdToUserId,
  useTestTreatmentV2,
} from '../../hooks/testTreatmentV2';
import { TEST_NAMES } from '../../constants/test-names';
import { useUserId } from '../../hooks/useUserId';

const SUPPLIER_MUTATION = gql`
  mutation createNewSupplier(
    $uuid: String!
    $name: String!
    $slug: String!
    $categories_tier1: String
    $state: enum_suppliers_state!
    $external_links: Json!
    $location_line_1: String
    $location_line_2: String
    $location_city: String
    $postcode: String
    $coordinates: Json
    $source: String
  ) {
    createSupplier(
      data: {
        uuid: $uuid
        name: $name
        slug: $slug
        state: $state
        categories_tier1: { connect: { sys_name: $categories_tier1 } }
        external_links: $external_links
        location_line_1: $location_line_1
        location_line_2: $location_line_2
        location_city: $location_city
        postcode: $postcode
        coordinates: $coordinates
        source: $source
      }
    ) {
      uuid
    }
  }
`;

const ADD_SUPPLIER_USER_MUTATION = gql`
  mutation addSupplierUser(
    $supplierUuid: String!
    $userId: String!
    $uuid: String!
  ) {
    createSupplierUser(
      data: {
        uuid: $uuid
        suppliers: { connect: { uuid: $supplierUuid } }
        users: { connect: { id: $userId } }
      }
    ) {
      uuid
    }
  }
`;

const Join = ({ tier1Verticals, user }) => {
  const [selectedTier1, setSelectedTier1] = useState('');
  const [createNewSupplier] = useMutation(SUPPLIER_MUTATION);
  const [addSupplierUser] = useMutation(ADD_SUPPLIER_USER_MUTATION);
  const { setAnonymousIdToUserId } = useAnonymousIdToUserId();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userId = useUserId(user.id);

  const multiVerticalPageTest = useTestTreatmentV2(
    userId.isAnonymousUser ? 'ANONYMOUS_ID' : 'USER_ID',
    userId.id,
    TEST_NAMES.MULTI_VERTICAL_PAGE_TEST
  );

  const showMultiVerticalPageTest =
    multiVerticalPageTest.testTreatment === 'on';

  const PAGE_NAME = 'Join Togather';
  const [showExitModal, setExitModal] = useState(false);
  const [stage, setStage] = useState(0);
  const stages = [
    'supplier_onboarding_welcome',
    'supplier_onboarding_tier1',
    'supplier_onboarding_name',
    'supplier_onboarding_location',
    'supplier_onboarding_external_links',
    'supplier_onboarding_source',
    'supplier_onboarding_user',
  ];
  const handleFormNext = stage => {
    setStage(stage + 1);
    trackEvent(SUPPLIER_FORM_NEXT_CLICKED, {
      stage: stages[stage],
    });
  };
  const handleFormBack = stage => {
    setStage(stage - 1);
    trackEvent(SUPPLIER_FORM_BACK_CLICKED, {
      stage: stages[stage],
    });
  };
  const displayExitModal = () => {
    setExitModal(true);

    trackEvent(SUPPLIER_FORM_CLOSE_CLICKED, {
      stage: stages[stage],
    });
  };
  const abandonSupplierForm = () => {
    trackEvent(SUPPLIER_FORM_ABANDONED, {
      stage: stages[stage],
    });
    window.location.href = paths.HOME;
  };
  const submitForm = async (values, actions) => {
    if (isSubmitting) {
      return;
    } else {
      setIsSubmitting(true);
    }

    let isComplete = false;

    const sanitizeSupplierForm = formValues => {
      Object.keys(formValues).forEach(key =>
        typeof formValues[key] === 'string'
          ? formValues[key].trim()
          : formValues[key]
      );
    };

    sanitizeSupplierForm(values.user);
    sanitizeSupplierForm(values.supplier);

    try {
      const createdUserId = await createSupplierUser(values.user);
      const createdSupplierId = await createSupplier(
        values.supplier,
        createNewSupplier
      );

      isComplete = await linkUserSupplier(
        createdUserId,
        createdSupplierId,
        addSupplierUser
      );

      if (
        createdUserId &&
        userId.isAnonymousUser &&
        showMultiVerticalPageTest
      ) {
        await setAnonymousIdToUserId({
          variables: {
            userId: createdUserId,
            anonymousId: cookies.get('ajs_anonymous_id'),
          },
        });
      }
    } catch (err) {
      if (err.response?.status === 403) {
        actions.setFieldError('user.email', 'Email already in use');
      }
    }

    if (isComplete) {
      const requestConversion = {
        category: SUPPLIER_APPLICATION_SUBMIT,
        action: 'Supplier Application Form Submission',
        label: 'Supplier Application Form',
        value: 1,
      };
      ReactGA.event(requestConversion);
      tagManagerTrack(SUPPLIER_APPLICATION_SUBMIT, requestConversion);

      window.location.href = paths.SUPPLIER_DASHBOARD_ONBOARDING;
    }
  };

  return (
    <Layout theme="base" showHeader={false} showFooter={false}>
      <ThemeProvider theme={togatherTheme}>
        <Meta subtitle={PAGE_NAME} path={paths.SUPPLIER_SIGN_UP_FORM} />
        <HideFreshChat />
        <SupplierContext.Provider
          value={{
            tier1Verticals,
          }}
        >
          <Wizard
            submitButtonText="Sign Up"
            withHeader
            position="fixed"
            top="0"
            right="0"
            bottom="0"
            left="0"
            px={{ _: 'gutter._', s: 'gutter.s', m: 'gutter.m' }}
            initialValues={{
              supplier: {
                state: 'pending',
                categories_tier1: '',
                name: '',
                external_links: {
                  website: '',
                  instagram: '',
                  other: '',
                },
                source: '',
                location_line_1: '',
                location_line_2: '',
                location_city: '',
                postcode: '',
                locationAutocomplete: { value: '', label: '' },
                manualLocation: false,
              },
              user: {
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                password: '',
                passwordConfirmation: '',
                terms: false,
              },
            }}
            onFormNext={handleFormNext}
            onFormBack={handleFormBack}
            onExit={displayExitModal}
            onSubmit={submitForm}
            isDisabled={isSubmitting}
          >
            <Wizard.Page stage="supplier_welcome" hideButtons={true}>
              <SupplierWelcome />
            </Wizard.Page>
            <Wizard.Page
              stage="supplier_service_tier1"
              hideButtons={false}
              validationSchema={tier1Schema}
            >
              <Tier1Service onSelection={setSelectedTier1} />
            </Wizard.Page>
            <Wizard.Page
              stage="supplier_business_name"
              validationSchema={nameSchema}
            >
              <BusinessName />
            </Wizard.Page>
            <Wizard.Page
              stage="supplier_location"
              validationSchema={locationSchema}
            >
              <SupplierLocation />
            </Wizard.Page>
            <Wizard.Page
              stage="supplier_social_accounts"
              validationSchema={externalLinksSchema(selectedTier1 === 'venues')}
            >
              <SocialMediaLinks />
            </Wizard.Page>
            <Wizard.Page
              stage="supplier_how_did_you_hear_about_feast_it"
              validationSchema={sourceSchema}
            >
              <HearingAboutFeastIt />
            </Wizard.Page>
            <Wizard.Page
              stage="supplier_personal_details"
              validationSchema={userSchema}
            >
              <SupplierDetails />
            </Wizard.Page>
          </Wizard>
        </SupplierContext.Provider>
        <Modal
          hideCloseButton
          open={showExitModal}
          handleClose={() => setExitModal(false)}
        >
          <Heading
            color="blueDark"
            fontSize="headingLargeMobile"
            lineHeight="headingLargeMobile"
            mb={6}
            fontWeight={500}
          >
            Want to leave the form?
          </Heading>

          <Flex alignItems="center" flexDirection="column">
            <ThemeProvider theme={baseTheme}>
              <Flex mb={2} width="100%">
                <Button
                  color="black"
                  variant="solid"
                  onClick={abandonSupplierForm}
                  isFullWidth={true}
                >
                  Yes, leave the form
                </Button>
              </Flex>

              <Button
                color="black"
                variant="outline"
                onClick={() => setExitModal(false)}
                isFullWidth={true}
              >
                No, continue with the form
              </Button>
            </ThemeProvider>
          </Flex>
        </Modal>
      </ThemeProvider>
    </Layout>
  );
};

Join.propTypes = {
  tier1Verticals: PropTypes.arrayOf(PropTypes.object),
  user: PropTypes.object.isRequired,
};

export const getServerSideProps = async () => {
  const tier1Verticals = await getTier1Verticals();

  return {
    props: {
      tier1Verticals,
    },
  };
};

export default Join;
